import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";

const SITE_NAME = "Anand Tutorials";
const SITE_URL = "https://anandtutorials.com";

// Module-level cache so we don't refetch override on every navigation between pages
const overrideCache = {};

function useSeoOverride(path) {
    const [override, setOverride] = useState(path ? overrideCache[path] : undefined);
    useEffect(() => {
        if (!path) return;
        if (overrideCache[path] !== undefined) {
            setOverride(overrideCache[path]);
            return;
        }
        api.get(`/seo?path=${encodeURIComponent(path)}`)
            .then((r) => {
                const o = r.data && r.data.path ? r.data : null;
                overrideCache[path] = o;
                setOverride(o);
            })
            .catch(() => {
                overrideCache[path] = null;
                setOverride(null);
            });
    }, [path]);
    return override;
}

export default function SEO({
    title,
    description,
    keywords,
    image,
    path = "",
    canonicalPath, // when set, used as the canonical URL (for keyword-alias routes)
    type = "website",
    schema,
    schemas, // optional array of additional JSON-LD blocks
    geo, // optional { lat, lng, region, placename, regionCode } for local SEO
    locale = "en_IN",
    noindex = false,
    hreflangs, // V45 — optional [{ lang: 'en-US', path: '/online-tuition-usa' }, ...]
    articlePublishedTime, // V45 — Article-type pages only
    articleModifiedTime,
    articleAuthor,
    articleSection,
}) {
    const override = useSeoOverride(canonicalPath || path);
    const finalTitle = (override && override.title) || title;
    const finalDescription = (override && override.description) || description;
    const finalKeywords = (override && override.keywords) || keywords;
    const finalImage = (override && override.og_image) || image;

    const fullTitle = finalTitle
        ? (finalTitle.includes(SITE_NAME) ? finalTitle : `${finalTitle} | ${SITE_NAME}`)
        : null;
    const canonicalUrl = `${SITE_URL}${canonicalPath || path}`;
    const img = finalImage || "https://anandtutorials.com/logo-anand-tutorials.png";

    // Build the JSON-LD list: legacy single `schema` prop OR array `schemas`
    const schemaList = [];
    if (schema) schemaList.push(schema);
    if (Array.isArray(schemas)) schemaList.push(...schemas.filter(Boolean));

    return (
        <Helmet>
            {fullTitle && <title>{fullTitle}</title>}
            {finalDescription && <meta name="description" content={finalDescription} />}
            {finalKeywords && <meta name="keywords" content={finalKeywords} />}
            {(canonicalPath || path) && <link rel="canonical" href={canonicalUrl} />}

            {/* Open Graph */}
            {fullTitle && <meta property="og:title" content={fullTitle} />}
            {finalDescription && <meta property="og:description" content={finalDescription} />}
            {fullTitle && <meta property="og:type" content={type} />}
            {(canonicalPath || path) && <meta property="og:url" content={canonicalUrl} />}
            {fullTitle && <meta property="og:image" content={img} />}
            {fullTitle && <meta property="og:site_name" content={SITE_NAME} />}
            {fullTitle && <meta property="og:locale" content={locale} />}

            {/* Twitter */}
            {fullTitle && <meta name="twitter:card" content="summary_large_image" />}
            {fullTitle && <meta name="twitter:title" content={fullTitle} />}
            {finalDescription && <meta name="twitter:description" content={finalDescription} />}
            {fullTitle && <meta name="twitter:image" content={img} />}

            {/* Robots */}
            <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
            <meta name="googlebot" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
            <meta name="bingbot" content={noindex ? "noindex, follow" : "index, follow"} />

            {/* V45 — hreflang alternates (international SEO) */}
            {Array.isArray(hreflangs) && hreflangs.map((h) => (
                <link key={h.lang} rel="alternate" hrefLang={h.lang} href={`${SITE_URL}${h.path}`} />
            ))}
            {/* V45 — auto-fallback x-default → canonical, unless the hreflangs
                array already provides one explicitly (V66). */}
            {Array.isArray(hreflangs) && hreflangs.length > 0 && !hreflangs.some((h) => h.lang === "x-default") && (
                <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
            )}

            {/* V45 — Article-type extras (BlogPosting / Article pages) */}
            {type === "article" && articlePublishedTime && (
                <meta property="article:published_time" content={articlePublishedTime} />
            )}
            {type === "article" && articleModifiedTime && (
                <meta property="article:modified_time" content={articleModifiedTime} />
            )}
            {type === "article" && articleAuthor && (
                <meta property="article:author" content={articleAuthor} />
            )}
            {type === "article" && articleSection && (
                <meta property="article:section" content={articleSection} />
            )}

            {/* GEO local-SEO signals — only emitted when geo prop is provided */}
            {geo && geo.region && <meta name="geo.region" content={geo.region} />}
            {geo && geo.placename && <meta name="geo.placename" content={geo.placename} />}
            {geo && geo.lat && geo.lng && (
                <meta name="geo.position" content={`${geo.lat};${geo.lng}`} />
            )}
            {geo && geo.lat && geo.lng && (
                <meta name="ICBM" content={`${geo.lat}, ${geo.lng}`} />
            )}

            {schemaList.map((s, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(s)}
                </script>
            ))}
        </Helmet>
    );
}
